/**
 * 粘贴框右下角那个**计数器**（"我粘了多少个字？"）—— 纯函数，有单测。
 *
 * ## 为什么要有它（2026-10-17 用户原话）
 *
 * > "还有一个 bug，就是对文字框里的**字数统计**，你应该用的是**字符**，而不是
 * >  **字数**，这里要修一下。"
 *
 * 改造前这一行是 `text.length` 个字符：用户那段 33 行、284 个汉字的内容显示成
 * **568 个字符**（多出来的是行内空格与换行），而下面预览写「生字 284 条」——
 * 两个数字对不上，他没法核对。
 *
 * ## 现在的口径（两个数，互相能解释）
 *
 * ```
 * 汉字 284 个（共 568 个字符，含空格/换行）
 * ```
 *
 *   · **前面的数** = 识别出的字/词元数，与预览里「生字 284 条 / 词语 0 条」**同口径**：
 *     字表用 `stats.charRows`、词语表用 `stats.wordRows`（没选表类型时用
 *     `stats.recognized`，说"条目"，**不猜**成字或词）；
 *   · **后面的数** = 原文的字符数（`text.length`，含分隔符与空白）。
 *
 * 两个数之所以能互相解释：每个识别出的条目至少占 1 个字符，条目之间还有分隔符 /
 * 换行，所以**字符数 ≥ 条目数**，差的正好是那些分隔符与空白。
 *
 * ## 为什么在这里本地跑一遍解析器（而不是问后端）
 *
 *   · 预览是一次网络 + 查库的请求，**为了显示一个字就发一次太贵**（用户会一边粘
 *     一边看这个数字）；
 *   · 但数字**不能各算一套**：这里调用的就是后端解析用的**同一个纯函数**
 *     `parseDictationImport`（`packages/shared`），并且把"这次发请求时会带的默认值"
 *     （表类型 / 默认课本 / 默认版次）原样传进去 —— 于是它算出来的 `charRows` /
 *     `wordRows` / `recognized` 与预览报告里的**是同一份算法、同一个输入**。
 *
 * ## 长文本的保护（⛔ 不要在每次按键上跑 50 万字的解析）
 *
 * 实测（本机）：解析 568 字符 ≈ 4 ms、2 000 字符 ≈ 5 ms、20 000 字符 ≈ 30 ms、
 * 240 000 字符 ≈ 270 ms；**24 万行左右还会把 `rows.push(...keptRows)` 的调用栈撑爆**。
 * 所以：
 *   · 超过 `IMPORT_COUNTER_PARSE_MAX` 个字符就**不解析**，只给字符数并说明
 *     "条目数在预览时统计"（预览本来就是这条路的正式出口）；
 *   · 组件侧用 `useDeferredValue` 把这一步让到下一帧（见 `useImportCounter.ts`），
 *     打字不会被它卡住。
 */
import { parseDictationImport } from '@fht/shared';

/** 计数器认得的表类型（`null` = 还没选：说"条目"，不替用户猜成字或词）。 */
export type ImportCounterListType = 'shizi' | 'xiezi' | 'cibiao' | null;

/**
 * 超过这么多个**字符**就不在本地跑解析（只显示字符数）。
 *
 * `20_000` 的依据：约 30 ms（见文件头实测），而用户实际会粘的是"几百上千字"
 * （≈ 几百到几千个字符）—— 这个上限离真实使用 ≥ 5 倍，同时把 50 万字符的
 * 极端情况挡在渲染路径之外。
 */
export const IMPORT_COUNTER_PARSE_MAX = 20_000;

export interface ImportCounterInput {
  /** 粘贴框里的原文（**不要 trim**：字符数就是它自己的长度）。 */
  text: string;
  /** 这次录的是哪张表；`null` = 还没选（中性措辞）。 */
  listType: ImportCounterListType;
  /**
   * 文本里没写课本时用的默认值。
   *
   * ⚠️ 必须与发请求时那份 payload 里的一致（`buildImportRequest` / 按书录入选中的
   *    那本书）：解析器的统计会受它影响，不一致就会与预览差一个数 ——
   *    而这块的全部意义就是"与预览对得上"。
   * ⛔ 版次已经彻底不用（2026-10-18），这里不再有 defaultEdition。
   */
  defaultTextbook?: string;
  /**
   * 已经拿到的**预览数字**（同一份文本时直接复用，省掉这次本地解析）。
   *
   * `null` / 省略 = 没有（自己解析）。⚠️ 只有"确认预览的就是当前这份文本"时才传：
   * 文本改过之后还用旧数字，正是用户抱怨的"两边对不上"。
   */
  reportedUnits?: number | null;
  /** 硬上限（导入面板要显示 500,000）——给了就写进括号里。 */
  limit?: number | null;
}

export interface ImportCounter {
  /** 识别出的字/词元数；`null` = 这次没统计（内容太长 / 解析失败）。 */
  units: number | null;
  /** 原文的字符数（含空格与换行）—— `text.length`。 */
  chars: number;
  /** 界面上那一行（**逐字定死**，测试锁文案）。 */
  label: string;
}

/** 计数器前面的**主语**（没选表类型时是"已识别 … 个条目"，**不猜**是字还是词）。 */
function counterSubject(listType: ImportCounterListType, units: number): string {
  const count = fmt(units);
  if (listType === 'shizi' || listType === 'xiezi') return `汉字 ${count} 个`;
  if (listType === 'cibiao') return `词语 ${count} 个`;
  return `已识别 ${count} 个条目`;
}

/** 千分位（与界面上其它计数同一种写法）。 */
function fmt(value: number): string {
  return value.toLocaleString('zh-CN');
}

/**
 * 计数器那一行的**文案**（唯一实现；界面不许自己拼）。
 *
 * 几种形状（⇐ 用户要求"未选表类型时用中性措辞，不要猜成字或词"）：
 *
 *   · `汉字 284 个（共 568 个字符，含空格/换行）`
 *   · `词语 8 个（共 30 个字符，含空格/换行）`
 *   · `已识别 284 个条目（共 568 个字符，含空格/换行）`
 *   · 空输入 → `0 个字符`
 *   · 没统计（太长 / 失败）→ `共 568,000 个字符（内容较长，条目数在预览时统计）`
 *
 * `limit` 给了就在括号里补「上限 N」（导入面板要显示 500,000 这个硬上限）。
 */
export function describeImportCounter(counter: {
  units: number | null;
  chars: number;
  listType: ImportCounterListType;
  limit?: number | null;
}): string {
  const limitSuffix =
    counter.limit === null || counter.limit === undefined
      ? ''
      : `，上限 ${fmt(counter.limit)}`;
  if (counter.units === null) {
    return `共 ${fmt(counter.chars)} 个字符（内容较长，条目数在预览时统计${limitSuffix}）`;
  }
  if (counter.chars === 0) return '0 个字符';
  return `${counterSubject(counter.listType, counter.units)}（共 ${fmt(
    counter.chars,
  )} 个字符，含空格/换行${limitSuffix}）`;
}

/**
 * 算一遍计数器（纯函数）。
 *
 * ⚠️ `units` 的口径（与预览同源，见文件头）：
 *   · 识字表 / 写字表 → `stats.charRows`（预览里"生字 N 条"那个数）；
 *   · 词语表           → `stats.wordRows`（预览里"词语 N 条"那个数）；
 *   · 没选表类型       → `stats.recognized`（"已识别 N 个条目"）。
 */
export function countImportText(input: ImportCounterInput): ImportCounter {
  const chars = input.text.length;

  const finish = (units: number | null): ImportCounter => ({
    units,
    chars,
    label: describeImportCounter({
      units,
      chars,
      listType: input.listType,
      ...(input.limit !== undefined ? { limit: input.limit } : {}),
    }),
  });

  /** 复用预览那一份（**同一份文本**时）——省掉一次本地解析。 */
  if (input.reportedUnits !== null && input.reportedUnits !== undefined) {
    return finish(input.reportedUnits);
  }

  if (input.text.trim() === '') return finish(0);

  /** 太长：不解析（见文件头"长文本的保护"）。 */
  if (chars > IMPORT_COUNTER_PARSE_MAX) return finish(null);

  try {
    const parsed = parseDictationImport(input.text, {
      defaultListType: input.listType,
      ...(input.defaultTextbook !== undefined && input.defaultTextbook !== ''
        ? { defaultTextbook: input.defaultTextbook }
        : {}),
    });
    if (input.listType === 'shizi' || input.listType === 'xiezi') {
      return finish(parsed.stats.charRows);
    }
    if (input.listType === 'cibiao') return finish(parsed.stats.wordRows);
    return finish(parsed.stats.recognized);
  } catch {
    /**
     * ⚠️ 解析器抛错时**不要让整个面板崩掉**（用户还在等你把字录进去）。
     *    这里退回"没统计"，预览仍然是权威口径。
     */
    return finish(null);
  }
}
